import { RouterLink as NextLink } from "../../router";
import type {
  DashboardHero,
  KpiTrend,
  KpiVerdict,
} from "@excellent/core";
import { Card, CardContent } from "../../card";
import { SectionTitle } from "../section-title";
import { MetricChart } from "../metric/metric-chart";
import type { MetricReference } from "../metric/types";
import type { Intent } from "../../tokens";

/**
 * HeroKpi — the Tier-1 dashboard hero. One North Star number, its target, a
 * verdict pill, and the optimization curve (KpiTrend). The single large element
 * on a department overview; everything below supports it.
 *
 * See docs/dashboard/DASHBOARD-REDESIGN-PRD.md §2/§4. Values arrive
 * pre-formatted (the provider runs the @excellent/ui formatters) so this stays a
 * pure presentation component. Inline SVG for the curve — no chart library on
 * the dashboard critical path (mirrors StatCard's sparkline + PrimaryLine).
 */

const VERDICT: Record<KpiVerdict, { intent: Intent; label: string }> = {
  improving: { intent: "success", label: "Improving" },
  "on-track": { intent: "success", label: "On track" },
  flat: { intent: "neutral", label: "Flat" },
  "off-track": { intent: "warning", label: "Off track" },
  regressing: { intent: "danger", label: "Regressing" },
};

export function HeroKpi({ hero }: { hero: DashboardHero }) {
  const verdict = VERDICT[hero.verdict];

  const body = (
    <CardContent className="flex flex-col gap-4 p-6">
      <SectionTitle
        variant="plain"
        size="md"
        title={hero.label}
        subtitle={hero.description}
        trend={{ label: verdict.label, intent: verdict.intent }}
      />

      <div className="flex items-baseline gap-3">
        <span className="type-heading font-medium tabular-nums text-foreground">
          {hero.value}
        </span>
        {hero.target ? (
          <span className="type-body text-muted-foreground">
            target {hero.target.display}
            {hero.target.label ? ` ${hero.target.label}` : ""}
          </span>
        ) : null}
      </div>

      <KpiCurve trend={hero.trend} verdict={hero.verdict} />

      {hero.delta ? (
        <div className="type-subtext text-muted-foreground tabular-nums">
          <span
            className={
              hero.delta.tone === "up"
                ? "text-success"
                : hero.delta.tone === "down"
                  ? "text-danger"
                  : "text-muted-foreground"
            }
          >
            {hero.delta.display}
          </span>{" "}
          vs last period · {verdict.label.toLowerCase()}
        </div>
      ) : null}
    </CardContent>
  );

  if (hero.href) {
    return (
      <NextLink
        href={hero.href}
        className="block rounded-lg transition-colors hover:bg-accent/20"
      >
        <Card className="py-0">{body}</Card>
      </NextLink>
    );
  }
  return <Card className="py-0">{body}</Card>;
}

/**
 * The optimization curve: the objective's series (solid brand line, area-filled)
 * with the target rendered as a dashed reference — a flat goal line, or a `pace`
 * polyline for cumulative targets (e.g. quota pace). The end-dot is tinted by
 * the verdict so "ahead of / behind target" reads at a glance, and the shading
 * is direction-aware via the verdict (down-good objectives already invert
 * upstream in the resolver). No axes; labels live in HTML around the SVG.
 */
function KpiCurve({
  trend,
  verdict,
}: {
  trend: KpiTrend;
  verdict: KpiVerdict;
}) {
  const pts = trend.points;
  if (pts.length === 0) {
    return (
      <div className="flex h-28 items-center justify-center rounded-md bg-muted/30 type-subtext text-muted-foreground">
        Establishing baseline — not enough history yet.
      </div>
    );
  }

  // Folded onto the shared MetricChart (../metric). The reference is a per-point
  // pace polyline if given, else a flat goal line, else none — MetricChart owns
  // the curve geometry + the verdict-tinted end-dot; the axis labels stay here
  // (composition, not chart).
  const reference: MetricReference =
    trend.target?.pace && trend.target.pace.length === pts.length
      ? { mode: "series", points: trend.target.pace, label: trend.target.label }
      : trend.target?.value !== undefined
        ? { mode: "flat-target", value: trend.target.value, label: trend.target.label }
        : { mode: "none" };

  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  const single = pts.length === 1;
  const summary = single
    ? `Current ${first.label}: ${first.display ?? first.value}, ${verdict}`
    : `${pts.length}-point trend, ${first.label} to ${last.label}, ${verdict}`;

  return (
    <div>
      <MetricChart
        history={pts.map((p) => p.value)}
        reference={reference}
        verdict={{ intent: VERDICT[verdict].intent, label: VERDICT[verdict].label }}
        className="h-32 w-full"
        ariaLabel={summary}
      />
      <div className="mt-2 flex items-center justify-between type-subtext text-muted-foreground tabular-nums">
        <span>{first.label}</span>
        {trend.target ? (
          <span className="text-muted-foreground/70">
            {trend.target.label ?? "target"}
          </span>
        ) : null}
        {single ? <span aria-hidden /> : <span>{last.label}</span>}
      </div>
    </div>
  );
}
